import { useEffect, useRef, useState } from "react";
import { type Identifier, useGetList, useNotify, useTranslate } from "ra-core";

import { useDealProfileSave } from "../dealProfile/useDealProfileSave";
import type {
  AiNoteChips,
  AiNoteSuggestionsResult,
  DealProfile,
  ProfileOption,
} from "../types";
import { AiSuggestionCard } from "./AiSuggestionCard";
import { useAiCopilot } from "./useAiCopilot";

const CATALOGUE_PAGINATION = { page: 1, perPage: 100 } as const;
const CATALOGUE_SORT = { field: "position", order: "ASC" as const };

/** Merges the proposed chips into the profile: single values replace, multi values are unioned. */
export function buildProfilePatch(
  chips: AiNoteChips,
  profile: DealProfile | null | undefined,
): Partial<DealProfile> {
  const patch: Partial<DealProfile> = {};
  if (chips.buyer_type_id != null) patch.buyer_type_id = chips.buyer_type_id;
  if (chips.credit_type_id != null) patch.credit_type_id = chips.credit_type_id;
  if (chips.timeline_id != null) patch.timeline_id = chips.timeline_id;
  if (chips.motivator_ids.length > 0) {
    patch.motivator_ids = [
      ...new Set([...(profile?.motivator_ids ?? []), ...chips.motivator_ids]),
    ];
  }
  if (chips.objection_ids.length > 0) {
    patch.objection_ids = [
      ...new Set([...(profile?.objection_ids ?? []), ...chips.objection_ids]),
    ];
  }
  return patch;
}

const hasChips = (chips: AiNoteChips): boolean =>
  Object.keys(buildProfilePatch(chips, null)).length > 0;

const useLabels = (resource: string): Map<string, string> => {
  const { data = [] } = useGetList<ProfileOption>(resource, {
    pagination: CATALOGUE_PAGINATION,
    sort: CATALOGUE_SORT,
  });
  return new Map(data.map((option) => [String(option.id), option.label]));
};

const chipLabels = (
  ids: (Identifier | null)[],
  labels: Map<string, string>,
): string[] =>
  ids
    .filter((id): id is Identifier => id != null)
    .map((id) => labels.get(String(id)))
    .filter((label): label is string => label != null);

/**
 * Almikia Epic EI (TASK-004), entry point (1): after a NEW note is saved on
 * a lead, asks the copilot once (on mount -- the parent mounts it only from
 * the note's save success) and shows the proposed profile chips and missing
 * questions. "Aplicar al perfil" goes through the existing profile save path
 * (`useDealProfileSave`); "Ahora no" discards. A failure (cap reached, AI
 * unavailable) is a short non-blocking notice and the card just closes.
 */
export function NoteAiSuggestion({
  dealId,
  noteId,
  onClose,
}: {
  dealId: Identifier;
  noteId: Identifier;
  onClose: () => void;
}) {
  const translate = useTranslate();
  const notify = useNotify();
  const { request, decide, isLoading, error } = useAiCopilot();
  const { profile, saveField } = useDealProfileSave(dealId);
  const [suggestion, setSuggestion] = useState<{
    id: Identifier;
    result: AiNoteSuggestionsResult;
  } | null>(null);
  const [busy, setBusy] = useState(false);
  const requestedRef = useRef(false);

  const buyerTypes = useLabels("profile_buyer_types");
  const creditTypes = useLabels("profile_credit_types");
  const timelines = useLabels("profile_timelines");
  const motivators = useLabels("profile_motivators");
  const objections = useLabels("profile_objections");

  useEffect(() => {
    if (requestedRef.current) return;
    requestedRef.current = true;
    void request({ action: "note_suggestions", dealId, noteId }).then(
      (response) => {
        if (response?.action !== "note_suggestions") return;
        const { chips, missing_items } = response.result;
        if (!hasChips(chips) && missing_items.length === 0) {
          onClose();
          return;
        }
        setSuggestion({ id: response.suggestionId, result: response.result });
      },
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!error) return;
    notify(error, { type: "warning" });
    onClose();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [error]);

  if (isLoading && !suggestion) {
    return (
      <p role="status" className="text-sm text-[#6B6F8A]">
        {translate("crm.ai.loading", { _: "Preparando sugerencia…" })}
      </p>
    );
  }
  if (!suggestion) return null;

  const { chips, missing_items, summary } = suggestion.result;
  const chipTexts = [
    ...chipLabels([chips.buyer_type_id], buyerTypes),
    ...chipLabels([chips.credit_type_id], creditTypes),
    ...chipLabels([chips.timeline_id], timelines),
    ...chipLabels(chips.motivator_ids, motivators),
    ...chipLabels(chips.objection_ids, objections),
  ];

  const finish = async (status: "accepted" | "edited" | "discarded") => {
    setBusy(true);
    try {
      if (status === "accepted") {
        await saveField(buildProfilePatch(chips, profile));
      }
      await decide(suggestion.id, { status });
      if (status === "edited") {
        notify("crm.ai.edit_hint", {
          type: "info",
          messageArgs: { _: "Ajusta el perfil del cliente más abajo" },
        });
      }
      onClose();
    } catch {
      notify("crm.ai.apply_error", {
        type: "warning",
        messageArgs: { _: "No se pudo aplicar al perfil. Intenta de nuevo." },
      });
      setBusy(false);
    }
  };

  return (
    <AiSuggestionCard
      because={summary}
      primaryLabel={translate("crm.ai.apply", { _: "Aplicar al perfil" })}
      onPrimary={() => void finish("accepted")}
      onEdit={() => void finish("edited")}
      onDismiss={() => void finish("discarded")}
      busy={busy}
    >
      {chipTexts.length > 0 ? (
        <div className="flex flex-col gap-2">
          <p className="font-semibold">
            {translate("crm.ai.chips_title", {
              _: "Datos propuestos para el perfil",
            })}
          </p>
          <ul className="flex flex-wrap gap-2">
            {chipTexts.map((text) => (
              <li
                key={text}
                className="rounded-full border border-input px-3 py-1 text-sm"
              >
                {text}
              </li>
            ))}
          </ul>
        </div>
      ) : null}
      {missing_items.length > 0 ? (
        <div className="mt-2 flex flex-col gap-1">
          <p className="font-semibold">
            {translate("crm.ai.questions_title", { _: "Preguntas pendientes" })}
          </p>
          <ul className="list-disc pl-5">
            {missing_items.map((item) => (
              <li key={item}>{item}</li>
            ))}
          </ul>
        </div>
      ) : null}
    </AiSuggestionCard>
  );
}
